import { useState, type RefObject } from 'react'
import type { PresetView, SceneApi } from '../scene/CoverScene'
import { SCENES, SCENE_NAMES } from '../lib/scenes'

const PRESETS: { id: PresetView; label: string }[] = [
  { id: 'front', label: 'Front' },
  { id: 'spine', label: 'Spine' },
  { id: 'back', label: 'Back' },
]

interface ViewControlsProps {
  apiRef: RefObject<SceneApi | null>
  autoSpin: boolean
  onToggleSpin: () => void
  onDownload: () => void
  onToggleHelp: () => void
  showHint: boolean
  currentScene: string
  onSelectScene: (name: string) => void
  showAr: boolean
  onViewAr: () => void
  showQr: boolean
  onShowQr: () => void
}

export function ViewControls({
  apiRef,
  autoSpin,
  onToggleSpin,
  onDownload,
  onToggleHelp,
  showHint,
  currentScene,
  onSelectScene,
  showAr,
  onViewAr,
  showQr,
  onShowQr,
}: ViewControlsProps) {
  // Start collapsed on small screens, where the dock can wrap to several rows and
  // crowd the preview; expanded on larger screens. Toggleable either way.
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  )

  if (collapsed) {
    return (
      <button
        className="dock__fab"
        onClick={() => setCollapsed(false)}
        title="Show controls"
        aria-label="Show controls"
        aria-expanded={false}
      >
        <span aria-hidden="true">▴</span> Controls
      </button>
    )
  }

  return (
    <>
      {showHint && <div className="hint">Drag to rotate · scroll to zoom</div>}

      <div className="dock">
        <button
          className="btn btn--icon"
          onClick={() => setCollapsed(true)}
          title="Hide controls"
          aria-label="Hide controls"
          aria-expanded={true}
        >
          ▾
        </button>

        <div className="dock__divider" />

        <div className="dock__group" role="group" aria-label="Camera views">
          {PRESETS.map((p) => (
            <button key={p.id} className="btn" onClick={() => apiRef.current?.setView(p.id)}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="dock__divider" />

        <select
          className="dock__select"
          value={currentScene}
          onChange={(e) => onSelectScene(e.target.value)}
          title="Background"
          aria-label="Background"
        >
          {SCENE_NAMES.map((name) => (
            <option key={name} value={name}>
              {SCENES[name].label}
            </option>
          ))}
        </select>

        <button
          className="btn btn--icon"
          onClick={() => apiRef.current?.resetView()}
          title="Reset view"
        >
          ⤺ Reset
        </button>
        <button
          className={`btn btn--icon ${autoSpin ? 'btn--active' : ''}`}
          onClick={onToggleSpin}
          title="Auto-spin"
          aria-pressed={autoSpin}
        >
          ⟳ Spin
        </button>
        {showAr && (
          <button className="btn btn--icon" onClick={onViewAr} title="View in AR / 3D">
            ▢ AR
          </button>
        )}
        {showQr && (
          <button className="btn btn--icon" onClick={onShowQr} title="View in AR on your phone">
            ▢ AR View
          </button>
        )}
        <button className="btn btn--icon" onClick={onDownload} title="Download image">
          ⤓ Save
        </button>
        <button className="btn btn--icon" onClick={onToggleHelp} title="Keyboard shortcuts" aria-label="Help">
          ?
        </button>
      </div>
    </>
  )
}

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="overlay overlay--center help" onClick={onClose}>
      <div className="help__card" onClick={(e) => e.stopPropagation()}>
        <div className="help__title">Keyboard shortcuts</div>
        <ul className="help__list">
          <li><kbd>←</kbd><kbd>→</kbd><kbd>↑</kbd><kbd>↓</kbd> rotate</li>
          <li><kbd>+</kbd><kbd>−</kbd> zoom</li>
          <li><kbd>[</kbd><kbd>]</kbd> light left / right</li>
          <li><kbd>;</kbd><kbd>'</kbd> light down / up</li>
          <li><kbd>R</kbd> reset view</li>
          <li><kbd>?</kbd> toggle this panel</li>
        </ul>
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </div>
  )
}
